import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HandbookModal } from './HandbookModal';
import { KUNJEAN_CREST_URL } from '../lib/kunjeanCrest';

// Global sticky top bar (mounted once via AppLayout). Marketing nav + CTAs on public
// pages; logo-only on /admin/* and /dashboard/* so those stay focused. Owns the
// HandbookModal (incl. the /handbook deep-link auto-open) and the mobile menu.
export function SiteHeader() {
  const { pathname } = useLocation();
  const isAuthPath = pathname.startsWith('/admin') || pathname.startsWith('/dashboard');

  const [handbookOpen, setHandbookOpen] = useState(
    typeof window !== 'undefined' && window.location.pathname === '/handbook',
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isHome = pathname === '/';
  const isHandbook = pathname === '/handbook';
  const isUpdates = pathname.startsWith('/updates');
  const isPapers = pathname.startsWith('/question-papers');
  const isJobs = pathname.startsWith('/teaching-jobs');
  const isGuides = pathname.startsWith('/strategy-guides');
  const isStories = pathname.startsWith('/success-stories');
  const isExamBuddy = pathname.startsWith('/exam-buddy');
  const isKunjeans = pathname.startsWith('/kunjeans');
  const closeMenu = () => setMenuOpen(false);
  const openHandbook = () => { setHandbookOpen(true); setMenuOpen(false); };

  return (
    <>
      <style>{`
        .sh-bar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; height: 76px; box-sizing: border-box;
          display: flex; align-items: center; gap: 1rem; padding: 0 1.25rem;
          background: linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%); border-bottom: 2px solid #d97706; font-family: system-ui, sans-serif; }
        .sh-center { flex: 1; display: flex; justify-content: center; gap: 2rem; }
        .sh-right { display: inline-flex; align-items: center; gap: 1rem; }
        .sh-navlink { color: #1a3a6b; text-decoration: none; font-size: 1.15rem; font-weight: 700; }
        .sh-navlink:hover { text-decoration: underline; }
        .sh-da { color: #5c1d0b; text-decoration: none; font-size: 13px; font-weight: 700; }
        .sh-da:hover { color: #1a3a6b; text-decoration: underline; }
        .sh-hb { background: #1a4480; color: #fff; padding: 8px 14px; border-radius: 6px; border: none; font-weight: 700; font-size: 13px; cursor: pointer; }
        .sh-hamburger { display: none; margin-left: auto; background: none; border: none; cursor: pointer; padding: 8px; }
        .sh-hamburger span { display: block; width: 22px; height: 2px; background: #1a4480; margin: 4px 0; border-radius: 2px; }
        .sh-menu { position: fixed; top: 76px; left: 0; right: 0; z-index: 99; background: #fffbeb;
          border-bottom: 1px solid #e5e7eb; box-shadow: 0 8px 20px rgba(0,0,0,0.08);
          display: flex; flex-direction: column; padding: 0.5rem 1.25rem 1rem; }
        @media (max-width: 699px) {
          .sh-center, .sh-right { display: none; }
          .sh-hamburger { display: block; }
        }
        @media (min-width: 700px) { .sh-menu { display: none; } }
        /* Exam Buddy nav highlight — pill background (same forest-green as its homepage hero
           tile and its own theme, lib/examBuddy/theme.ts) so it stands out from the plain-text
           nav links, plus the same smooth-pulse dot convention as LatestAlertsBanner's 🔔 dot.
           A real on/off blink is avoided deliberately (WCAG 2.2.2). */
        .sh-eb-pill { background: linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%) !important; color: #ffffff !important;
          padding: 0.45rem 0.9rem; border-radius: 999px; box-shadow: 0 2px 8px rgba(27,67,50,0.4); }
        .sh-eb-pill:hover { text-decoration: none; filter: brightness(1.12); }
        @keyframes shExamBuddyPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(1.35); } }
        .sh-eb-dot { width: 8px; height: 8px; border-radius: 50%; background: #ffffff; flex-shrink: 0; animation: shExamBuddyPulse 1.4s ease-in-out infinite; }
        /* Kunjeans nav highlight — same pill treatment as Exam Buddy above, so it gets
           equal visual weight, but in the Kunjean crest's maroon/saffron so the two
           pills stay easy to tell apart at a glance (matches shreyaadi.co.in's own
           nav-kunjeans-btn, ported here for parity). */
        .sh-kunjeans-pill { background: linear-gradient(135deg, #7c2d12 0%, #b45309 100%) !important; color: #ffffff !important;
          padding: 0.45rem 0.9rem; border-radius: 999px; box-shadow: 0 2px 8px rgba(124,45,18,0.4); }
        .sh-kunjeans-pill:hover { text-decoration: none; filter: brightness(1.12); }
        .sh-kunjeans-dot { width: 8px; height: 8px; border-radius: 50%; background: #ffffff; flex-shrink: 0; animation: shExamBuddyPulse 1.4s ease-in-out infinite; }
      `}</style>

      <header className="sh-bar" style={{ boxShadow: scrolled ? '0 2px 8px rgba(0,0,0,0.06)' : 'none' }}>
        <Link to="/" onClick={closeMenu} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', color: '#1a4480', flexShrink: 0 }}>
          {/* Single unified brand crest — founder photo in the SA octagon frame with the
              SA badge fused on the lower rim. Same mark in the hero, social cards and app
              icon. brand-crest.webp is a new filename, so no cache-bust needed. */}
          <img src="/brand-crest.webp" alt="Shrey Aadi" width={50} height={50} style={{ width: 50, height: 50, objectFit: 'contain', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
          <span style={{ fontWeight: 800, fontSize: '1.15rem', color: '#1a3a6b' }}>Shrey Aadi</span>
        </Link>

        {!isAuthPath && (
          <>
            <nav className="sh-center">
              <Link to="/" className="sh-navlink" style={{ fontWeight: isHome ? 700 : 600 }}>Find a Mentor</Link>
              <Link to="/updates" className="sh-navlink" style={{ fontWeight: isUpdates ? 700 : 600 }}>Updates</Link>
              <Link to="/question-papers" className="sh-navlink" style={{ fontWeight: isPapers ? 700 : 600 }}>Question Papers</Link>
              <Link to="/teaching-jobs" className="sh-navlink" style={{ fontWeight: isJobs ? 700 : 600 }}>Teaching Jobs</Link>
              <Link to="/strategy-guides" className="sh-navlink" style={{ fontWeight: isGuides ? 700 : 600 }}>Strategy Guides</Link>
              <Link to="/success-stories" className="sh-navlink" style={{ fontWeight: isStories ? 700 : 600 }}>Success Stories</Link>
              <Link to="/kunjeans" className="sh-navlink sh-kunjeans-pill" style={{ fontWeight: isKunjeans ? 700 : 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                <img src={KUNJEAN_CREST_URL} alt="" width={16} height={18} style={{ display: 'inline-block' }} /> Kunjeans
                <span aria-hidden="true" className="sh-kunjeans-dot" />
              </Link>
              <Link to="/exam-buddy" className="sh-navlink sh-eb-pill" style={{ fontWeight: isExamBuddy ? 700 : 600, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                Exam Buddy
                <span aria-hidden="true" className="sh-eb-dot" />
              </Link>
              <Link to="/handbook" className="sh-navlink" style={{ fontWeight: isHandbook ? 700 : 600 }} onClick={openHandbook}>Free Career Handbook</Link>
            </nav>
            <div className="sh-right">
              <a className="sh-da" href="https://shreyaadi.co.in" target="_blank" rel="noopener noreferrer">Defence Academy ↗</a>
            </div>
            <button className="sh-hamburger" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
              <span /><span /><span />
            </button>
          </>
        )}
      </header>

      {!isAuthPath && menuOpen && (
        <div className="sh-menu">
          <Link to="/" className="sh-navlink" style={{ padding: '0.7rem 0', fontWeight: isHome ? 700 : 600 }} onClick={closeMenu}>Find a Mentor</Link>
          <Link to="/updates" className="sh-navlink" style={{ padding: '0.7rem 0', fontWeight: isUpdates ? 700 : 600 }} onClick={closeMenu}>Updates</Link>
          <Link to="/question-papers" className="sh-navlink" style={{ padding: '0.7rem 0', fontWeight: isPapers ? 700 : 600 }} onClick={closeMenu}>Question Papers</Link>
          <Link to="/teaching-jobs" className="sh-navlink" style={{ padding: '0.7rem 0', fontWeight: isJobs ? 700 : 600 }} onClick={closeMenu}>Teaching Jobs</Link>
          <Link to="/strategy-guides" className="sh-navlink" style={{ padding: '0.7rem 0', fontWeight: isGuides ? 700 : 600 }} onClick={closeMenu}>Strategy Guides</Link>
          <Link to="/success-stories" className="sh-navlink" style={{ padding: '0.7rem 0', fontWeight: isStories ? 700 : 600 }} onClick={closeMenu}>Success Stories</Link>
          <Link to="/kunjeans" className="sh-navlink sh-kunjeans-pill" style={{ fontWeight: isKunjeans ? 700 : 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem', margin: '0.35rem 0', alignSelf: 'flex-start' }} onClick={closeMenu}>
            <img src={KUNJEAN_CREST_URL} alt="" width={16} height={18} style={{ display: 'inline-block' }} /> Kunjeans
            <span aria-hidden="true" className="sh-kunjeans-dot" />
          </Link>
          <Link to="/exam-buddy" className="sh-navlink sh-eb-pill" style={{ fontWeight: isExamBuddy ? 700 : 600, display: 'inline-flex', alignItems: 'center', gap: '0.35rem', margin: '0.35rem 0', alignSelf: 'flex-start' }} onClick={closeMenu}>
            Exam Buddy
            <span aria-hidden="true" className="sh-eb-dot" />
          </Link>
          <Link to="/handbook" className="sh-navlink" style={{ padding: '0.7rem 0', fontWeight: isHandbook ? 700 : 600 }} onClick={openHandbook}>Free Career Handbook</Link>
          <a className="sh-da" href="https://shreyaadi.co.in" target="_blank" rel="noopener noreferrer" style={{ padding: '0.7rem 0' }} onClick={closeMenu}>Defence Academy ↗</a>
        </div>
      )}

      {handbookOpen && <HandbookModal onClose={() => setHandbookOpen(false)} />}
    </>
  );
}
